"use client";

import Image from "next/image";
import { useState } from "react";
import { Play } from "lucide-react";
import { projectVideos, youtubeThumb } from "@/lib/v2/video";
import { VideoLightbox } from "./VideoLightbox";
import type { ProjectProperty } from "./types";

/** YouTube thumbnails in a grid; click opens the full-screen player. */
export function ProjectVideos({ property }: { property: ProjectProperty }) {
  const videos = projectVideos(property);
  const [active, setActive] = useState<string | null>(null);
  const label = property.title || property.name;

  if (videos.length === 0) return null;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {videos.map((video, i) => (
          <button
            key={`${i}-${video.id}`}
            type="button"
            onClick={() => setActive(video.id)}
            aria-label={`Play video ${i + 1} of ${videos.length}`}
            className="group relative aspect-video overflow-hidden rounded-2xl bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <Image src={youtubeThumb(video.id)} alt={`Video ${i + 1}`} fill unoptimized className="object-cover transition-transform duration-300 group-hover:scale-[1.03]" sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex size-14 items-center justify-center rounded-full bg-white text-ink shadow-[0_8px_30px_rgba(23,29,46,0.25)] transition-colors group-hover:bg-surface">
                <Play aria-hidden="true" className="ml-0.5 size-6 fill-current" />
              </span>
            </span>
          </button>
        ))}
      </div>

      <VideoLightbox id={active} onClose={() => setActive(null)} label={`${label} video`} />
    </>
  );
}
